import Typography from '@mui/material/Typography';
import { endOfWeek, isAfter, isSameDay } from 'date-fns';
import { useCreateHabitAllowed } from 'features/tasks';
import { useUpdateHabitGroupBy, useUpdateHabitSortMode } from 'features/user';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TopBarBase } from 'shared/components/connected/top-bar-base';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { WeekSelector } from 'shared/components/ui/week-selector';
import { useOpenCreateHabitDialog } from 'shared/contexts/habit-form';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { useTheme } from 'styled-components';

import {
  useNextTimeframe,
  usePreviousTimeframe,
  useResetTimeframe,
  useWeekDate,
} from '../../contexts/timeframe';
import { HeaderSortButton } from './header-sort-button';
import * as Styled from './page-top-bar.style';

export const PageTopBar: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const createHabitAllowed = useCreateHabitAllowed();
  const openCreateHabitDialog = useOpenCreateHabitDialog();
  const openPremiumDialog = useOpenPremiumDialog();
  const setSortMode = useUpdateHabitSortMode();
  const setGroupBy = useUpdateHabitGroupBy();

  const createHabit = () =>
    createHabitAllowed ? openCreateHabitDialog() : openPremiumDialog();

  const weekDate = useWeekDate();
  const onPrev = usePreviousTimeframe();
  const onNext = useNextTimeframe();
  const onReset = useResetTimeframe();
  const nextDisabled = useMemo(
    () =>
      isSameDay(endOfWeek(weekDate), endOfWeek(today)) ||
      isAfter(endOfWeek(weekDate), endOfWeek(today)),
    [today, weekDate],
  );

  return (
    <TopBarBase
      mobileNavigationSlot={
        <WeekSelector
          date={weekDate}
          onNext={onNext}
          onNextDisabled={nextDisabled}
          onPrev={onPrev}
          onDate={onReset}
          weekStartsOn={weekStartsOn}
          preventDrag
        />
      }
    >
      <Styled.Container>
        <Styled.Title>
          <Typography variant="h5" component="h1">
            {t('pages.habits.title')}
          </Typography>
        </Styled.Title>

        <Styled.CenterInteractions>
          <WeekSelector
            date={weekDate}
            onNext={onNext}
            onNextDisabled={nextDisabled}
            onPrev={onPrev}
            onDate={onReset}
            weekStartsOn={weekStartsOn}
            preventDrag
          />
        </Styled.CenterInteractions>

        <Styled.InteractionsContainer>
          <HeaderSortButton
            onSort={setSortMode}
            onGroupBy={setGroupBy}
            sort={user?.settings?.habitSortingMode}
            groupBy={user?.settings?.habitGroupBy}
          />
          {!theme.isMobile && (
            <Button
              onClick={createHabit}
              size={Sizes.Small}
              variant={Variants.Contained}
              color={Colors.Primary}
              preventDrag
            >
              {t('pages.habits.header.add-habit')}
            </Button>
          )}
        </Styled.InteractionsContainer>
      </Styled.Container>
    </TopBarBase>
  );
};
